import styles from './PositionsTable.module.scss';

import { Box, Tooltip } from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { useUser } from '@work4all/data';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';
import { ErpPositionSlMode } from '@work4all/models/lib/Enums/ErpPositionSlMode.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';
import { canViewERPPrices } from '@work4all/utils/lib/permissions';

import { settings, useSetting } from '../../../../../../../../../../settings';
import { useFormContextPlus } from '../../../../../../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../../../../../../hooks/mask-context';
import { useErpPositionsAutomaticNumberingContext } from '../../../../../contexts/ErpPositionsAutomaticNumberingContext';
import { ErpData } from '../../../../../ErpData';
import { ShadowObjectAddPositionArgs } from '../../../../../hooks/use-bz-shadow-object-api';
import { useShadowBzObjectApiContext } from '../../../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';
import { EditTable } from '../edit-table/EditTable';
import { STUECKLISTE_GROUP } from '../edit-table/hooks/use-editable-state';
import {
  EditTableColumns,
  EditTableEntry,
  EditTableProps,
  IdArray,
  IEditTable,
  OnEditPosition,
} from '../edit-table/types';
import { useSelectedPositionState } from '../hooks/use-selected-position';
import { PositionMask } from '../position-mask/PositionMask';
import { handleOrderChange } from '../utils/handle-order-change';

import { defaultErpGetRelation } from './defaultErpGetRelation';
import { PositionsMobileTable } from './PositionsMobileTable';

export const ForbiddenPriceCell: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('ALERTS.NOT_AUTHORIZED_TO_SEE_PRICES')}>
      <Box textAlign="center">-</Box>
    </Tooltip>
  );
};

export type ForwardedEditTableProps = Pick<
  EditTableProps<Position>,
  'showSelectionColumn' | 'refs' | 'showBorders' | 'allowedColumns'
>;

export type IPositionsTableProps = {
  disabled?: boolean;
  showPositionMask?: boolean;
  positions: Position[];
  onMovePosition: (id: string, index: number) => void;
  onPickPosition?: (article?: Article) => void;
  onRemovePosition: (positionId: IdArray) => void;
  onAddPosition?: (props: ShadowObjectAddPositionArgs) => void;
  onEditPosition: (result: OnEditPosition<Position>) => void;
  onCollapsePosition: (result: Position) => void;
  tableInstanceRef: React.RefObject<TableInstance | null>;
  columns: EditTableColumns<Position>[];
  refs?: {
    editTable: React.RefObject<IEditTable>;
  };
  total: number;
  singleLine?: boolean;
} & ForwardedEditTableProps;

export const PositionsTable = (props: IPositionsTableProps) => {
  const {
    disabled = false,
    positions,
    onAddPosition,
    onEditPosition,
    onCollapsePosition,
    showPositionMask = true,
    showBorders = true,
    columns,
    allowedColumns,
    showSelectionColumn,
    singleLine = true,
    total,
  } = props;

  const { t } = useTranslation();
  const { pending, loading } = useShadowBzObjectApiContext();

  const mask = useMaskContext();

  const user = useUser();
  const { watch } = useFormContextPlus<ErpData>();
  const currency = watch('currency');
  const { setSelectedPositionIds } = useSelectedPositionState();

  const showERPPrices = canViewERPPrices(user);

  const renderMobileFooter = useCallback(() => {
    return (
      <tr className={clsx(styles.title, styles.mobilePositionRow)}>
        <td></td>
        <td></td>
        <td></td>
        <td className={clsx(styles['tr-border'], styles.mobilePositionRow)}>
          {showERPPrices && (
            <>
              <span style={{ float: 'right' }}>
                {formatNumberAsCurrency(total)} {formatCurrency(currency, t)}
              </span>

              <div style={{ whiteSpace: 'pre-line' }}>{t('COMMON.TOTAL')}</div>
            </>
          )}
        </td>
      </tr>
    );
  }, [currency, total, t, showERPPrices]);

  const classes = useMemo(
    () => ({
      mobile: styles.mobilePositionsTable,
      thead: styles.thead,
      tbody: styles.tbody,
      tfoot: styles.tfoot,
      additionalHeaders: styles.dragHandler,
    }),
    []
  );

  const loadingRows = useMemo(
    () =>
      (pending.type === 'modifyPosition' &&
        ['singlePriceNet', 'amount', 'discount'].some((x) =>
          pending.properties.includes(x)
        )) ||
      pending.type === 'movePosition'
        ? [pending.positionId]
        : [],
    [pending]
  );
  const { isRecordAutomaticNumberingEnabled } =
    useErpPositionsAutomaticNumberingContext();

  const isRowEditable = useMemo(
    () => !pending.pending && pending.type !== 'addPosition',
    [pending]
  );

  const decorators = useMemo(() => {
    const isEditableCell = (cell: string, original: Position) => {
      if (cell === 'number') {
        return !isRecordAutomaticNumberingEnabled;
      }

      if (
        (original.positionKind === ErpPositionsKind.INTERNE_POSITION ||
          original.positionKind === ErpPositionsKind.INTERNE_STUECKLISTE) &&
        cell === 'singlePriceNet'
      )
        return false;

      if (
        cell === 'longtext' &&
        STUECKLISTE_GROUP.includes(original.positionKind) &&
        original.partsListType ===
          ErpPositionSlMode.STUECKLISTE_MIT_SUB_POSITIONEN_IM_LANGTEXT
      ) {
        return false;
      }

      const relation = defaultErpGetRelation(original);
      if (cell === 'amount' && relation === 'child') {
        return false;
      }
      if (relation === 'parent') {
        return !['partsListAmount', 'singlePriceNet'].includes(cell);
      }

      return true;
    };

    return { getRelation: defaultErpGetRelation, isEditableCell };
  }, [isRecordAutomaticNumberingEnabled]);

  const onRowOut = useCallback(
    (position: Position, dir: 1 | -1) => {
      const notCachedPositions = positions.filter(
        (x) => !(x as EditTableEntry).cacheOnly
      );
      const isLast =
        notCachedPositions[notCachedPositions.length - 1]?.id === position?.id;
      if (isLast) {
        if (showBorders)
          onAddPosition({
            positionType: ErpPositionsKind.TEXTZEILE,
            article: { id: 0 } as Article,
          });
        setTimeout(() => {
          props.refs.editTable.current.setEditMode('undefined', 'longtext', [
            'longtext',
          ]);
        }, 100);
      } else {
        const positionIndex = notCachedPositions.findIndex(
          (x) => x.id === position?.id
        );
        const nextPosition = notCachedPositions[positionIndex + dir];
        if (nextPosition)
          props.refs.editTable.current.setEditMode(
            nextPosition.id.toString(),
            'longtext'
          );
      }
      // scroll to the left to see longtext popover
      if (props.refs.scroll?.current) props.refs.scroll.current.scrollLeft = 0;
    },
    [
      positions,
      props.refs.scroll,
      props.refs.editTable,
      showBorders,
      onAddPosition,
    ]
  );
  const erpSize = useSetting(settings.erpRowSize());

  const columnSettings = useSetting(settings.erpPositionsConfig(mask.entity));
  const noIdEditableRows = useMemo(
    () => ['longtext', 'amount', 'shortText', 'unit', 'internalText'],
    []
  );

  const editableRows = useMemo(() => {
    const editableLongtex = ['longtext'];
    const editableWithNumber = ['longtext'];
    if (!isRecordAutomaticNumberingEnabled) {
      editableWithNumber.push('number');
    }

    return {
      TITEL: editableWithNumber,
      TITELSUMME: editableLongtex,
      ZWISCHENSUMME: editableLongtex,
      TEXTZEILE: editableLongtex,
      UMWANDLUNGSHISTORIE: editableWithNumber,
    };
  }, [isRecordAutomaticNumberingEnabled]);

  const renderMobilePosition = useCallback(
    (
      position: Position,
      setEditedPosition: (position: Position) => void,
      allPositions: Position[]
    ) => {
      return (
        <PositionsMobileTable
          position={position}
          setEditedPosition={setEditedPosition}
          allPositions={allPositions}
          columns={columns}
          decorators={decorators}
          editableRows={editableRows}
        />
      );
    },
    [columns, decorators, editableRows]
  );

  return !loading ? (
    <Box display="flex" flex="1" flexDirection="column" overflow="hidden">
      <EditTable<Position>
        // Editable state
        onAddPosition={props.onAddPosition}
        onRemovePosition={props.onRemovePosition}
        onEditPosition={onEditPosition}
        onMovePosition={props.onMovePosition}
        onCollapsePosition={onCollapsePosition}
        onDropItem={props.onPickPosition}
        // refs
        ref={props.refs.editTable}
        refs={props.refs}
        tableInstanceRef={props.tableInstanceRef}
        // mask & mobile
        renderMobileItem={renderMobilePosition}
        renderMobileFooter={renderMobileFooter}
        mask={showPositionMask ? PositionMask : undefined}
        // Others
        disabled={disabled}
        columns={columns}
        items={positions}
        editableRows={editableRows}
        isRowEditable={isRowEditable}
        onSelectedItemIdsChange={setSelectedPositionIds}
        classes={classes}
        loadingRows={loadingRows}
        itemSize="auto"
        noIdEditableCells={noIdEditableRows}
        decorators={decorators}
        showBorders={showBorders}
        showSelectionColumn={showSelectionColumn}
        allowedColumns={allowedColumns}
        onRowOut={onRowOut}
        columnSettings={columnSettings}
        handleOrderChange={handleOrderChange}
        rowSizeMode={singleLine ? erpSize.value : 'AUTO'}
        entity={Entities.position}
        bordersKey="ERP"
      />
    </Box>
  ) : null;
};
